/**
 * Lightweight, privacy-friendly page-view tracker.
 * Skips embedded (Shopify iframe), /admin, /app, /api paths, and preview/localhost.
 */
import { API_BASE } from "../lib/apiBase";

const VISITOR_KEY = "rd_visitor_id";
const SESSION_KEY = "rd_session_id";

const genId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "id-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
};

const getVisitorId = () => {
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = genId();
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch {
    return "";
  }
};

const getSessionId = () => {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = genId();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return "";
  }
};

const shouldSkip = (path) => {
  if (!path) return true;
  if (path.startsWith("/admin")) return true;
  if (path.startsWith("/api")) return true;
  if (path.startsWith("/app")) return true;
  // Skip if Shopify embedded (has host param in URL)
  try {
    const p = new URLSearchParams(window.location.search);
    if (p.get("host") || p.get("embedded") === "1") return true;
  } catch { /* ignore */ }
  return false;
};

export const trackPageView = (path) => {
  if (shouldSkip(path)) return;

  const payload = {
    path,
    referrer: document.referrer || "",
    visitor_id: getVisitorId(),
    session_id: getSessionId(),
    screen: `${window.innerWidth}x${window.innerHeight}`,
  };

  const url = `${API_BASE}/api/track`;
  const body = JSON.stringify(payload);

  // Prefer sendBeacon for reliability (fires even during unload)
  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(url, blob);
      return;
    }
  } catch { /* fall through to fetch */ }

  fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
    credentials: "include",
  }).catch(() => { /* fail silently */ });
};
